import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

export function MemberSignInCopy({
  value,
  label,
  text = false,
}: {
  value: string
  label: string
  text?: boolean
}) {
  const [status, setStatus] = useState<'idle' | 'pending' | 'copied' | 'failed'>('idle')
  const active = useRef(true)
  const busy = useRef(false)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  useEffect(() => {
    if (status !== 'copied') return
    const timer = setTimeout(() => setStatus('idle'), 2000)
    return () => clearTimeout(timer)
  }, [status])
  async function copy() {
    if (busy.current) return
    busy.current = true
    setStatus('pending')
    try {
      await navigator.clipboard.writeText(value)
      if (active.current) setStatus('copied')
    } catch {
      if (active.current) setStatus('failed')
    } finally {
      busy.current = false
    }
  }
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={text ? 'outline' : 'ghost'}
            size={text ? 'default' : 'icon'}
            aria-label={label}
            disabled={status === 'pending'}
            onClick={() => void copy()}
          >
            {text ? (
              <>
                {status === 'copied' && (
                  <Check className="mr-1.5 size-3.5 text-success" strokeWidth={1.5} />
                )}
                Copy
              </>
            ) : status === 'copied' ? (
              <Check className="size-3.5 text-success" strokeWidth={1.5} />
            ) : (
              <Copy className="size-3.5" strokeWidth={1.5} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Copy</TooltipContent>
      </Tooltip>
      <span
        role="status"
        className={status === 'failed' ? 'text-caption text-destructive' : 'sr-only'}
      >
        {status === 'copied'
          ? 'Copied'
          : status === 'failed'
            ? 'Could not copy. Please try again.'
            : ''}
      </span>
    </>
  )
}
